import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import {
  Timestamp,
  IDriver,
  LicensesEnum,
  IDriverPropertyValidated,
} from "../types";
import { query, doc } from "firebase/firestore";
import {
  validateEmail,
  validateMobile,
  validateStringLength,
} from "../helpers/validationHelper";
import { getApiToken } from "../data/api/token";
import {
  createDriver as createDriverApi,
  updateDriver as updateDriverApi,
  deleteDriver as deleteDriverApi,
} from "../data/api/drivers";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class Driver implements IDriver {
  id: string;
  workspaceId: string;
  companyId: string;
  firstName: string;
  lastName: string;
  email: string;
  licenses: LicensesEnum[];
  vehicleId?: string;
  allowedVehicleIds?: string[];
  phoneNumber?: string;
  createdAt?: Timestamp | null;
  onDuty?: boolean;
  trackingOn?: boolean;
  dayStartedAt?: Timestamp | string | null;
  dayEndedAt?: Timestamp | string | null;
  startAddress?: string;
  startCity?: string;
  startZipCode?: string;

  static collectionName = "drivers";

  static converter = {
    toFirestore(driver: Driver) {
      return driver.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as IDriver;
      return new Driver({ ...data, id: snapshot.id });
    },
  };

  static createId() {
    return firestore.collection(Driver.collectionName).doc().id;
  }

  /**
   * Canonical driver display name: "first last" (trimmed). When the name is
   * empty and `fallbackToEmail` is true, returns the driver's email instead.
   */
  static getDriverName(
    driver?: IDriver | null,
    fallbackToEmail = false,
  ): string {
    if (!driver) return "";
    const name = `${driver.firstName || ""} ${driver.lastName || ""}`.trim();
    if (name) return name;
    return fallbackToEmail ? driver.email || "" : "";
  }

  constructor({
    id,
    workspaceId,
    companyId,
    firstName,
    lastName,
    email,
    licenses,
    vehicleId: vehcileId,
    allowedVehicleIds,
    phoneNumber,
    createdAt,
    onDuty,
    trackingOn,
    dayStartedAt,
    dayEndedAt,
    startAddress,
    startCity,
    startZipCode,
  }: IDriver) {
    this.id = id || "";
    this.workspaceId = workspaceId || "";
    this.companyId = companyId || "";
    this.firstName = firstName || "";
    this.lastName = lastName || "";
    this.email = email || "";
    this.licenses = licenses || [];
    this.vehicleId = vehcileId || undefined;
    this.allowedVehicleIds = allowedVehicleIds;
    this.phoneNumber = phoneNumber || undefined;
    this.createdAt = createdAt;
    this.onDuty = onDuty;
    this.trackingOn = trackingOn;
    this.dayStartedAt = dayStartedAt;
    this.dayEndedAt = dayEndedAt;
    this.startAddress = startAddress || undefined;
    this.startCity = startCity || undefined;
    this.startZipCode = startZipCode || undefined;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<IDriver>) {
    return Object.assign(this, updates);
  }

  data() {
    return {
      workspaceId: this.workspaceId,
      companyId: this.companyId,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.email,
      licenses: this.licenses || [],
      vehicleId: this.vehicleId || null,
      ...(this.allowedVehicleIds !== undefined
        ? { allowedVehicleIds: this.allowedVehicleIds }
        : {}),
      phoneNumber: this.phoneNumber || null,
      createdAt: this.createdAt || null,
      startAddress: this.startAddress || null,
      startCity: this.startCity || null,
      startZipCode: this.startZipCode || null,
    };
  }

  static default() {
    return new Driver({
      id: "",
      workspaceId: "",
      companyId: "",
      firstName: "",
      lastName: "",
      email: "",
      licenses: [],
      vehicleId: undefined,
      createdAt: undefined,
    });
  }

  static isNew(data: IDriver): boolean {
    return !data?.id;
  }

  static get = (id: string) => {
    return doc(
      firestore
        .collection(Driver.collectionName)
        .withConverter(Driver.converter),
      id,
    );
  };

  static list = (workspaceId: string) => {
    return query(
      firestore
        .collection(Driver.collectionName)
        .where("workspaceId", "==", workspaceId)
        .withConverter(Driver.converter),
    );
  };

  static validateDriver(driver: IDriver) {
    let validationValues = {
      firstName: validateStringLength(driver.firstName, 2),
      lastName: validateStringLength(driver.lastName, 2),
      phoneNumber: validateMobile(driver.phoneNumber || ""),
      email: validateEmail(driver.email || ""),
    } as IDriverPropertyValidated;

    let invalid = Object.entries({
      ...validationValues,
    });
    return {
      isValidDriver: invalid.filter(([key, value]) => !value).length === 0,
      invalidFields: Object.fromEntries(invalid),
    };
  }

  static create = async (
    workspaceId: string,
    data: Omit<IDriver, "id" | "createdAt">,
  ) => {
    const newDriver = new Driver({
      ...data,
      workspaceId: workspaceId,
      id: "",
    });
    try {
      const token = await getApiToken();
      await createDriverApi(token, workspaceId, newDriver.data());
      return true;
    } catch (e) {
      console.warn("Create driver failed with error: ", e);
      return false;
    }
  };

  static update = async (driver: Driver, updates: Partial<IDriver>) => {
    const updateType =
      (driver.clone && driver.clone()) || new Driver({ ...driver });

    updateType.setData({
      ...updates,
    });

    const token = await getApiToken();
    const updated = await updateDriverApi(
      token,
      updateType.workspaceId,
      driver.id,
      updateType.data(),
    );

    return new Driver(updated);
  };

  static remove = async (driver: Driver) => {
    if (!driver.id) {
      throw new Error(
        "Cannot delete a legacy driver before the API has hydrated its document id",
      );
    }
    const token = await getApiToken();
    return await deleteDriverApi(token, driver.workspaceId, driver.id);
  };
}

export default Driver;
